import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import InlineAlert from "../ui/InlineAlert";
import Skeleton from "../ui/Skeleton";
import { formatWhen } from "./reviewDisplay";

function reviewStartError(err) {
  const code = err?.response?.data?.code;
  if (code === "REPOSITORY_NOT_CONNECTED") return "Connect this repository first.";
  if (code === "PR_NOT_FOUND" || code === "PR_FORBIDDEN") return "GitHub access is missing for that pull request.";
  if (code === "PR_CLOSED") return "That pull request is already closed.";
  if (code === "REVIEW_CREDIT_REQUIRED") return "You need one Review Credit to start this review.";
  if (code === "PRIVATE_INSTALLATION_REQUIRED") return "Connect GitHub to review private repositories.";
  return err?.response?.data?.error || "Review could not be started.";
}

function safeGithubUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "github.com" && url.pathname.startsWith("/apps/");
  } catch {
    return false;
  }
}

export default function PendingPullRequests({
  mutateAllowed = true,
  repositoryId = "",
  repositoryIds = [],
  onStarted,
}) {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({
    publicationEnabled: false,
    truncated: false,
    skippedRepos: 0,
    rateLimited: false,
    setupMessage: null,
  });
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState("");
  const [startingId, setStartingId] = useState("");
  const [rowError, setRowError] = useState("");
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return undefined;
    loaded.current = true;
    let cancelled = false;
    Promise.all([
      api.get("/workspace/pull-requests"),
      api.get("/workspace/setup").catch(() => ({ data: {} })),
    ])
      .then(([prs, setupResponse]) => {
        if (cancelled) return;
        setItems(prs.data?.pullRequests || []);
        setMeta({
          publicationEnabled: Boolean(prs.data?.publicationEnabled),
          truncated: Boolean(prs.data?.truncated),
          skippedRepos: Number(prs.data?.skippedRepos || 0),
          rateLimited: Boolean(prs.data?.rateLimited),
          setupMessage: prs.data?.setupMessage || null,
        });
        setSetup(setupResponse.data || {});
      })
      .catch(() => {
        if (!cancelled) {
          setError("Open pull requests could not be loaded from GitHub.");
          setItems([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canPublishToGithub = Boolean(meta.publicationEnabled && setup?.publicationPossible);
  const allowedIds = repositoryIds.length
    ? repositoryIds.map(String)
    : repositoryId
      ? [String(repositoryId)]
      : [];
  const visible = (items || []).filter(
    (item) => !allowedIds.length || allowedIds.includes(String(item.repoId || ""))
  );

  async function connectGitHub() {
    setRowError("");
    try {
      const response = await api.get("/github-app/install-url");
      const url = response.data?.installUrl;
      if (!safeGithubUrl(url)) throw new Error("unsafe");
      window.location.assign(url);
    } catch {
      setRowError("GitHub connection is not available right now.");
    }
  }

  async function startReview(pull) {
    if (!mutateAllowed || startingId) return;
    if (pull.action === "open_review" && pull.existingReviewId) {
      onStarted?.({ id: pull.existingReviewId, status: pull.existingReviewStatus }, true);
      return;
    }
    setRowError("");
    setStartingId(pull.id);
    try {
      const response = await api.post("/workspace/decision-reviews", {
        pullRequestUrl: pull.htmlUrl,
      });
      const review = response.data?.review;
      const existing =
        response.status === 200 && review?.status !== "analyzing" && review?.status !== "queued";
      onStarted?.(review, existing);
    } catch (err) {
      setRowError(reviewStartError(err));
    } finally {
      setStartingId("");
    }
  }

  if (items === null && !error) {
    return (
      <section className="rr-pending-prs" data-testid="pending-pull-requests">
        <Skeleton label="Loading open pull requests" />
      </section>
    );
  }

  return (
    <section className="rr-pending-prs" data-testid="pending-pull-requests">
      <div className="rr-section-heading">
        <div>
          <span className="rr-eyebrow">Open on GitHub</span>
          <h2>{visible.length ? "Pending pull requests" : "No open pull requests yet"}</h2>
        </div>
        {visible.length ? <span className="rr-muted">{visible.length} open</span> : null}
      </div>
      <p className="rr-pending-note">
        {canPublishToGithub
          ? "Click Review to run it in RenderRepo and publish the result as a GitHub Check on that pull request."
          : "Click Review to run it in RenderRepo. Connect the GitHub App with Checks permission to also post the result on the pull request."}
      </p>
      {!canPublishToGithub && mutateAllowed ? (
        <InlineAlert>
          GitHub Checks are not ready yet.{" "}
          <button type="button" className="rr-text-button" onClick={connectGitHub}>
            Connect GitHub
          </button>{" "}
          so completed reviews also appear on the pull request.
        </InlineAlert>
      ) : null}
      {meta.setupMessage ? <InlineAlert>{meta.setupMessage}</InlineAlert> : null}
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      {rowError ? <InlineAlert tone="alert">{rowError}</InlineAlert> : null}
      {!visible.length && !error ? (
        <div className="rr-inbox-empty">
          <h3>{repositoryId ? "No open pull requests for this repository" : "Nothing waiting for review"}</h3>
          <p>
            {meta.skippedRepos > 0
              ? "Some repositories need GitHub connected before their pull requests can appear here."
              : "When a connected repository has an open pull request, it will show up here for one-click review."}
          </p>
          {mutateAllowed && meta.skippedRepos > 0 ? (
            <button type="button" className="rr-button secondary" onClick={connectGitHub}>
              Connect GitHub
            </button>
          ) : null}
          <p>
            <Link to="/settings#repositories">Manage codebases</Link>
          </p>
        </div>
      ) : null}
      {visible.length ? (
        <div className="rr-pending-table">
          <div className="rr-pending-head">
            <span>Pull request</span>
            <span>Updated</span>
            <span className="rr-sr">Action</span>
          </div>
          {visible.map((pull) => {
            const busy = startingId === pull.id;
            const label = pull.actionLabel || (pull.action === "open_review" ? "Open review" : pull.action === "review_update" ? "Review update" : "Review");
            const openExisting = pull.action === "open_review" && pull.existingReviewId;
            return (
              <div key={pull.id} className="rr-pending-row">
                <div className="rr-pending-pr">
                  <strong>
                    {pull.owner}/{pull.name} #{pull.pullNumber}
                  </strong>
                  <span className="rr-muted">
                    {pull.title}
                    {pull.author ? ` · ${pull.author}` : ""}
                    {pull.headRef ? ` · ${pull.headRef}` : ""}
                  </span>
                </div>
                <span className="rr-when">{formatWhen(pull.updatedAt) || "—"}</span>
                {mutateAllowed ? (
                  openExisting ? (
                    <Link className="rr-button secondary rr-pending-action" to={`/reviews/${pull.existingReviewId}`}>
                      {label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="rr-button rr-pending-action"
                      disabled={Boolean(startingId)}
                      onClick={() => startReview(pull)}
                    >
                      {busy ? "Starting…" : label}
                    </button>
                  )
                ) : (
                  <span className="rr-muted">View only</span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {meta.truncated ? (
        <p className="rr-muted rr-pending-truncated">Showing the most recently updated open pull requests.</p>
      ) : null}
    </section>
  );
}
